import { useRef, useEffect } from "react";
import { useMotionValue, useSpring } from "framer-motion";

/**
 * Magnetic hover effect — element subtly follows cursor when hovered.
 * Returns a ref to attach to the element and motion values for x/y transform.
 */
export function useMagnetic(strength = 0.3) {
	const ref = useRef(null);
	const x = useMotionValue(0);
	const y = useMotionValue(0);
	const springX = useSpring(x, { damping: 20, stiffness: 200 });
	const springY = useSpring(y, { damping: 20, stiffness: 200 });

	useEffect(() => {
		const el = ref.current;
		if (!el) return;

		// Disable on touch devices
		if (window.matchMedia('(pointer: coarse)').matches) return;

		const handleMove = (e) => {
			const rect = el.getBoundingClientRect();
			const centerX = rect.left + rect.width / 2;
			const centerY = rect.top + rect.height / 2;
			const deltaX = (e.clientX - centerX) * strength;
			const deltaY = (e.clientY - centerY) * strength;
			x.set(deltaX);
			y.set(deltaY);
		};

		const handleLeave = () => {
			x.set(0);
			y.set(0);
		};

		el.addEventListener('mousemove', handleMove, { passive: true });
		el.addEventListener('mouseleave', handleLeave, { passive: true });

		return () => {
			el.removeEventListener('mousemove', handleMove);
			el.removeEventListener('mouseleave', handleLeave);
		};
	}, [strength, x, y]);

	return { ref, x: springX, y: springY };
}
